import fscreen from "fscreen";
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { IGif } from "services/types";
import { metrics } from "utils/metrics";

interface IUseFullScreen {
  ref: any;
}

const SCROLL_DELAY = 500;

export const useFullScreen = ({ ref }: IUseFullScreen) => {
  const location = useLocation();
  const [inFullscreenMode, setInFullscreenMode] = useState(false);
  const [FSGif, setFSGif] = useState<IGif | null>(null);
  const WrapEL = document.querySelector(".Wrapper");

  useEffect(() => {
    if (fscreen.fullscreenEnabled) {
      fscreen.addEventListener("fullscreenchange",handleFullscreenChange, false);
      fscreen.addEventListener("fullscreenerror", handleFullscreenError, false);

      return () => {
        fscreen.removeEventListener("fullscreenchange", handleFullscreenChange);
        fscreen.removeEventListener("fullscreenerror", handleFullscreenError);
      };
    }
  });

  useEffect(() => {
    if (inFullscreenMode) {
      if (document.body) {
        document.body.classList.add("fullScreen");
      }
      metrics.sendEnterFullscreen();
    } else if (document.body) {
      document.body.classList.remove("fullScreen");
    }
  }, [inFullscreenMode]);

  useEffect(() => {
    return () => {
      if (document.fullscreenElement && document.hasFocus()) {
        try {
          fscreen.exitFullscreen();
        } catch (e) {
          console.error(`Could not exit fullscreen: ${e}`);
        }
      }

      if (document.body) {
        document.body.classList.remove("fullScreen");
      }

    };
  }, [location]);

  const handleGoToGif = useCallback((id: string | undefined) => {
    if (id) {
      WrapEL?.querySelector(`#${id}`)?.scrollIntoView();
    }
  }, [WrapEL]);

  const getActiveElement = useCallback(() => {
    const activeElement = ref.current.querySelector(".active");
    const adActiveElement = ref.current.querySelector("[data-videoads=adsVideo]");
    let activeGif: string | undefined = undefined;

    if (adActiveElement) {
      activeGif = adActiveElement.closest(".player").previousSibling.id;
    }

    if (activeElement) {
      activeGif = activeElement.id;
    }

    return activeGif;
  }, [ref]);

  const handleFullscreenChange = useCallback((e: any) => {
    if (fscreen.fullscreenElement !== null) {
      console.debug("Entered fullscreen mode", e);
      setInFullscreenMode(true);
    } else {
      console.debug("Exited fullscreen mode", e);
      const activeGif = getActiveElement();

      setInFullscreenMode(false);
      setTimeout(() => handleGoToGif(activeGif), SCROLL_DELAY);
    }
  }, [handleGoToGif, getActiveElement]);

  const handleFullscreenError = useCallback((e: any) => {
    console.debug("Fullscreen Error", e);
  }, []);

  const toggleFullscreen = useCallback((gif: IGif | null) => {
    const fsEnabled = fscreen.fullscreenEnabled;

    if (inFullscreenMode && !fsEnabled) {
      setFSGif(null);
      setInFullscreenMode(false);
      const activeGif = getActiveElement();
      setTimeout(() => handleGoToGif(activeGif), SCROLL_DELAY);
    }

    if (!inFullscreenMode && !fsEnabled){
      setFSGif(gif);
      setInFullscreenMode(true);
    }

    if (fsEnabled && inFullscreenMode) {
      setFSGif(null);
      if (document.hasFocus() && document.fullscreenElement) {
        try {
          fscreen.exitFullscreen();
        } catch (e) {
          console.error(`Could not exit fullscreen: ${e}`);
        }
      }
    }

    if (fsEnabled && !inFullscreenMode) {
      setFSGif(gif);
      if (ref.current) {
        try {
          fscreen.requestFullscreen(ref.current);
        } catch (e) {
          console.error(`Could not enter fullscreen: ${e}`);
        }
      }
    }
  }, [inFullscreenMode, ref, handleGoToGif, getActiveElement]);

  return {
    inFullscreenMode,
    toggleFullscreen,
    FSGif,
    fullscreenEnabled: fscreen.fullscreenEnabled
  };
};
